import { useCallback, useEffect, useRef, useState } from 'react'

export type LoadingPhase = 'hidden' | 'visible' | 'exiting'

type LoadingVisibilityOptions = {
  delayMs?: number
  minimumVisibleMs?: number
  exitMs?: number
}

type TimerRef = {
  current: number | null
}

function clearTimer(timer: TimerRef) {
  if (timer.current === null) return

  window.clearTimeout(timer.current)
  timer.current = null
}

export function useLoadingVisibility(
  pending: boolean,
  {
    delayMs = 200,
    minimumVisibleMs = 500,
    exitMs = 100,
  }: LoadingVisibilityOptions = {},
): LoadingPhase {
  const [phase, setPhase] = useState<LoadingPhase>('hidden')
  const phaseRef = useRef<LoadingPhase>('hidden')
  const visibleAtRef = useRef<number | null>(null)
  const exitingAtRef = useRef<number | null>(null)
  const appearanceTimerRef = useRef<number | null>(null)
  const minimumTimerRef = useRef<number | null>(null)
  const exitTimerRef = useRef<number | null>(null)

  const updatePhase = useCallback((nextPhase: LoadingPhase) => {
    phaseRef.current = nextPhase
    setPhase(nextPhase)
  }, [])

  useEffect(() => {
    clearTimer(appearanceTimerRef)
    clearTimer(minimumTimerRef)
    clearTimer(exitTimerRef)

    const finishExit = () => {
      exitTimerRef.current = null
      visibleAtRef.current = null
      exitingAtRef.current = null
      updatePhase('hidden')
    }

    const beginExit = () => {
      exitingAtRef.current = Date.now()
      updatePhase('exiting')
      exitTimerRef.current = window.setTimeout(finishExit, exitMs)
    }

    if (pending) {
      if (phaseRef.current === 'exiting') {
        exitingAtRef.current = null
        updatePhase('visible')
      } else if (phaseRef.current === 'hidden') {
        appearanceTimerRef.current = window.setTimeout(() => {
          appearanceTimerRef.current = null
          visibleAtRef.current = Date.now()
          updatePhase('visible')
        }, delayMs)
      }

      return
    }

    if (phaseRef.current === 'exiting') {
      const exitingAt = exitingAtRef.current ?? Date.now()
      const elapsedExitMs = Date.now() - exitingAt
      const remainingExitMs = exitMs - elapsedExitMs

      if (remainingExitMs > 0) {
        exitTimerRef.current = window.setTimeout(finishExit, remainingExitMs)
      } else {
        finishExit()
      }

      return
    }

    if (phaseRef.current === 'visible') {
      const visibleAt = visibleAtRef.current ?? Date.now()
      const elapsedVisibleMs = Date.now() - visibleAt
      const remainingVisibleMs = minimumVisibleMs - elapsedVisibleMs

      if (remainingVisibleMs > 0) {
        minimumTimerRef.current = window.setTimeout(() => {
          minimumTimerRef.current = null
          beginExit()
        }, remainingVisibleMs)
      } else {
        beginExit()
      }
    }
  }, [delayMs, exitMs, minimumVisibleMs, pending, updatePhase])

  useEffect(
    () => () => {
      clearTimer(appearanceTimerRef)
      clearTimer(minimumTimerRef)
      clearTimer(exitTimerRef)
    },
    [],
  )

  return phase
}
